import environment from '../environment';
import { ErrorCode } from './error-code-utils';
import { ariaLabelWithNewTab } from './a11y-util';
import { getCartSamplingErrorMessage } from '../../features/instructor-sample/utils/instructor-sample-error.util';
import { html, nothing, TemplateResult } from 'lit';

export interface ErrorMessageTypeConstants {
  bold?: boolean;
  element?: HTMLElement;
  errorCode?: ErrorCode;
  internalServerError?: boolean;
}

/**
 * Returns a specific error message based on the error code returned by Magento APIs.
 * If not found, it returns the fallback error message.
 *
 * @param errorMessageTypeConstants
 */
export function getApiErrorMessage(errorMessageTypeConstants: ErrorMessageTypeConstants): TemplateResult {
  switch (errorMessageTypeConstants.errorCode) {
    case ErrorCode.MIXED_OC_RMS_PRODUCT:
    case ErrorCode.MHP_MIXED_CART_ERROR_CODE:
      return getMixedCartErrorMessage();
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_BOARDS_BEYOND:
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_CLINICAL_REASONING:
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_FIRST_AID_FORWARD:
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_K12:
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_MHP:
    case ErrorCode.HE_INSTRUCTOR_CART_RESTRICTION_SHARPEN: {
      const samplingError = getCartSamplingErrorMessage(
        errorMessageTypeConstants.errorCode,
        errorMessageTypeConstants.element
      );
      return samplingError !== nothing ? samplingError : getFallbackErrorMessage(errorMessageTypeConstants);
    }
    default:
      return getFallbackErrorMessage(errorMessageTypeConstants);
  }
}

function getFallbackErrorMessage(errorMessageTypeConstants: ErrorMessageTypeConstants): TemplateResult {
  return html`
    ${errorMessageTypeConstants.internalServerError
      ? 'We apologize for the inconvenience, but we’ve experienced an error'
      : 'That didn’t seem to work... We’re sorry for the inconvenience'}.
    Please try again, and if the problem persists reach out to
    <a
      class="${errorMessageTypeConstants.bold ? `fw-bold` : ``}"
      href="${environment().customerServiceUrl}"
      target="_blank"
      aria-label="${ariaLabelWithNewTab('customer support')}">
      customer support</a
    >.
  `;
}

function getMixedCartErrorMessage(): TemplateResult {
  return html`
    This product cannot be purchased together with the items in your cart. Please remove one or more items from your
    cart and try again.
  `;
}
